import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Typography,
  Grid,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import AdminSidebar from "../../Container/AdminSidebar/AdminSidebar";

const PendingApproualUser = () => {
  const [users, setUsers] = useState([]);
  const userDataString = localStorage.getItem("Login_User_Community_Details");
  const token = JSON.parse(userDataString);
  const [showUnapproved, setShowUnapproved] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchUsers = () => {
    setLoading(true);
    const url = `${API_BASE_URL}/api/unapproved-users${
      showUnapproved ? "?status=Pending" : ""
    }`;
    if (token) {
      axios
        .get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          const usersWithChecked = response.data.map((user) => ({
            ...user,
            checked: false,
          }));

          setUsers(response.data);
          setUsers(usersWithChecked);
        })
        .catch((error) => {
          console.error("Error fetching users:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      window.location.href = "/login";
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [showUnapproved]);

  // Handle checkbox toggle
  const handleCheckboxChange = (id) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === id ? { ...user, checked: !user.checked } : user
      )
    );
  };

  // Approve user
  const handleApprove = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You are about to approve this user.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(
            `${API_BASE_URL}/api/users/${id}/approve`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((response) => {
            Swal.fire("Approved!", response.data.message, "success");
            setUsers(users.filter((user) => user.id !== id));
          })
          .catch((error) => {
            Swal.fire("Error!", "Something went wrong.", "error");
            console.error("Error approving user:", error);
          });
      }
    });
  };

  // Reject user
  const handleReject = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You are about to reject this user.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, reject it!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(
            `${API_BASE_URL}/api/users/${id}/reject`,
            {}, // Request body if needed, leave as empty object if not
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((response) => {
            Swal.fire("Rejected!", response.data.message, "success");
            setUsers(users.filter((user) => user.id !== id));
          })
          .catch((error) => {
            Swal.fire("Error!", "Something went wrong.", "error");
            console.error("Error rejecting user:", error);
          });
      }
    });
  };

  const handleSuspend = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You are about to suspend this user.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ffa726", // Warning color for suspend
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, suspend it!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(
            `${API_BASE_URL}/api/users/${id}/suspend`,
            {}, // Request body if needed, leave as an empty object if not
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((response) => {
            Swal.fire("Suspended!", response.data.message, "success");
            setUsers(
              users.map((user) =>
                user.id === id ? { ...user, status: "Suspended" } : user
              )
            );
          })
          .catch((error) => {
            Swal.fire("Error!", "Something went wrong.", "error");
            console.error("Error suspending user:", error);
          });
      }
    });
  };

  const handleRevertSuspend = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You are about to revert this user's suspension.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, revert it!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(
            `${API_BASE_URL}/api/users/${id}/approve`,
            {}, // Empty request body
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((response) => {
            Swal.fire("Reverted!", response.data.message, "success");
            // Update user's suspension status locally
            setUsers((prevUsers) =>
              prevUsers.map((user) =>
                user.id === id ? { ...user, suspension_status: false } : user
              )
            );
          })
          .catch((error) => {
            Swal.fire("Error!", "Something went wrong.", "error");
            console.error("Error reverting suspension:", error);
          });
      }
    });
  };

  return (
    <Box
      sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8f9fa" }}
    >
      <Box sx={{ flexGrow: 1, padding: "20px" }}>
        <Typography variant="h4" gutterBottom>
          All Users
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={showUnapproved}
              onChange={(e) => setShowUnapproved(e.target.checked)}
            />
          }
          label="Show Unapproved Users Only"
        />
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={3}
            sx={{
              mt: 3,
              overflowX: "auto", // Enable horizontal scrolling
            }}
          >
            <Table stickyHeader aria-label="responsive table">
              <TableHead sx={{ backgroundColor: "#f1f1f1" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Contact No</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.contact_no}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {showUnapproved ? (
                          <Grid container spacing={1}>
                            <Grid item>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handleApprove(user.id)}
                              >
                                Approve
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleReject(user.id)}
                              >
                                Reject
                              </Button>
                            </Grid>
                          </Grid>
                        ) : (
                          <Grid container spacing={1}>
                            {user.status === "Suspend" ? (
                              <Grid item>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={() => handleRevertSuspend(user.id)}
                                >
                                  Revert Suspension
                                </Button>
                              </Grid>
                            ) : user.status === "Approval" ? (
                              <Grid item>
                                <Button
                                  variant="contained"
                                  color="warning"
                                  size="small"
                                  onClick={() => handleSuspend(user.id)}
                                >
                                  Suspend
                                </Button>
                              </Grid>
                            ) : null}
                          </Grid>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body1" color="textSecondary">
                        No users found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

        )}
      </Box>
    </Box>
  );
};

export default PendingApproualUser;
